import { TRIGGER_FAMILIES, TRIGGER_FAMILY_LABELS } from '@/api/automation'
import type { ModuleKey } from '@/api/types'
import { NotLivePage, useNotLivePage } from '@/app/notLive'
import { Card, EmptyState, LoadingBlock } from '@/design'

import './automation.css'
import { useAutomationQuery } from './hooks'

/**
 * The plan module a trigger needs. Not derived from `SECTIONS`: several
 * sections share the `core_analytics` entitlement, so looking it up by
 * matching `section.entitlement` would resolve ambiguously to whichever
 * section happens to be listed first.
 */
const MODULE_LABELS: Record<ModuleKey, string> = {
  core_analytics: 'Analytics',
  insights: 'Insights',
  marketing_intelligence: 'Marketing',
  automation: 'Automation',
  competition_operations: 'Competition Management',
  market_intelligence: 'Market Intelligence',
}

/** Automation > Triggers: the catalogue of events and thresholds a workflow can start from, grouped by family. */
export function TriggersPage() {
  const notLive = useNotLivePage('triggerDefinitions')
  const triggers = useAutomationQuery('trigger-definitions', (client) => client.triggerDefinitions())
  if (notLive) return <NotLivePage />

  if (triggers.isPending) return <LoadingBlock rows={6} />
  if (triggers.isError) {
    return (
      <Card>
        <EmptyState variant="outage" compact description="Triggers could not be loaded." />
      </Card>
    )
  }

  const definitions = triggers.data ?? []

  return (
    <div className="ci-stack">
      {TRIGGER_FAMILIES.map((family) => {
        const items = definitions.filter((definition) => definition.family === family)
        if (items.length === 0) return null
        return (
          <Card key={family} title={TRIGGER_FAMILY_LABELS[family]}>
            <dl className="au-trigger-list">
              {items.map((definition) => (
                <div key={definition.key} className="au-trigger-list__item">
                  <dt>{definition.label}</dt>
                  <dd>{definition.description}</dd>
                  <dd className="au-trigger-list__module">Needs {MODULE_LABELS[definition.module]}</dd>
                </div>
              ))}
            </dl>
          </Card>
        )
      })}
    </div>
  )
}
